import { useTranslation } from 'react-i18next';
import {
  Stack,
  Typography,
  GraphicsUnicornImage,
  GraphicsFreelanceImage,
  BigVariantButton,
  Skeleton,
  ErrorListItem,
} from '@razz-team/ui-web';
import { useCallback, useEffect } from 'react';
import { useUpdateVerificationFlowTypeMutation } from '../../../../api/kyc.ts';
import { useIdempotencyKey } from '../../../../hooks/useIdempotencyKey.ts';
import { ApiVerificationFlowType } from '../../../../openapi';

export function KybFlowStep({ onSuccess }: { onSuccess?: () => void }) {
  const { t } = useTranslation();
  const idempotencyKey = useIdempotencyKey();
  const mutation = useUpdateVerificationFlowTypeMutation({ idempotencyKey });

  useEffect(() => {
    if (mutation.isSuccess) {
      onSuccess?.();
    }
  }, [mutation.isSuccess, onSuccess]);

  const handleFlowChange = useCallback(
    (flow: ApiVerificationFlowType) => mutation.mutate({ flow }),
    [mutation],
  );

  useEffect(() => {
    if (!__BRIGHTY__ && mutation.isIdle) {
      mutation.mutate({ flow: 'COMPANY' });
    }
  }, [mutation]);

  if (!__BRIGHTY__) return null;

  return (
    <Stack alignItems="center" justifyContent="center" spacing={6}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={6}>
        <BigVariantButton
          variant="COMPANY"
          title={t('Company')}
          description={t('You have a stable business and regular financial transactions')}
          graphics={GraphicsUnicornImage}
          onClick={handleFlowChange}
        />
        <BigVariantButton
          variant="FREELANCER"
          title={t('Freelance')}
          description={t('You have small cash turnovers and many different counterparties')}
          graphics={GraphicsFreelanceImage}
          onClick={handleFlowChange}
        />
      </Stack>
      {mutation.isError ? (
        <ErrorListItem error={mutation.error} />
      ) : (
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {mutation.isPending ? (
            <Skeleton width="100%" />
          ) : (
            t('Click the option that suits you to continue')
          )}
        </Typography>
      )}
    </Stack>
  );
}
